import { Checkbox, Col, Form } from 'antd';
import SidePanel from 'common/side-panel/side-panel';
import Spinner from 'common/spinner/components/spinner';
import moment from 'utils/cops-moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import styled from 'styled-components';
import { Button, Dropdown, FormField, Input, Row } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import RichTextEditor from '../../../ui/rich-text-editor';
import {
  announcementPublishOptions,
} from '../../../utils/app-configs';
import { getFullDateFromFormValues, isObjectNullOrEmpty } from '../../../utils/helpers';
import {
  addAnnouncement,
  fetchAnnouncement,
  fetchBannerAnnouncement,
  fetchWizardData,
  resetAnnouncementData,
  updateAnnouncement,
} from '../../containers/announcements/announcements-actions';
import PublishOptionSelector from './publish-option-selector';
import SeveritySelector from './severity-selector';

const StyledButton = styled(Button)`
  text-transform: uppercase;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
`;

const StyledRow = styled(Row)`
  margin-bottom: 20px;
`;

const Buttons = styled(Row)`
  gap: 20px;
`;

const EditAnnouncementSidepanel = ({
  intl,
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const {
    updateAnnouncementSuccess,
    isUpdatingAnnouncement,
    isLoadingWizardData,
    wizardData,
    announcement = {},
    isLoadingAnnouncement,
  } = useSelector(state => state.announcements);
  const selectedPublishOption = Form.useWatch('announcementPublishing', form);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const isSidePanelLoading =
    isLoadingAnnouncement ||
    isLoadingWizardData ||
    (params?.id && isObjectNullOrEmpty(announcement));

  const handleTemplateChange = (templateId) => {
    const template = wizardData?.templates?.find(t => t?.id === templateId);
    if (template) {
      form.setFieldsValue({
        template: template.id,
        title: template.title,
        message: template.message,
      });
    }
  };

  useEffect(() => {
    dispatch(fetchWizardData());
  }, []);

  useEffect(() => {
    if (params.id) {
      dispatch(fetchAnnouncement(params.id));
    }
  }, [params.id]);

  useDeepCompareEffect(() => {
    if (!isObjectNullOrEmpty(announcement)) {
      let publishOptionToSelect;
      let startDate = null;
      let endDate = null;

      if (announcement.isPublished) {
        if (announcement.startDate && announcement.endDate) {
          publishOptionToSelect = announcementPublishOptions.specific;
          startDate = {
            date: moment(announcement.startDate),
            hour: moment(announcement.startDate),
            minute: moment(announcement.startDate),
          };
          endDate = {
            date: moment(announcement.endDate),
            hour: moment(announcement.endDate),
            minute: moment(announcement.endDate),
          };
        } else {
          publishOptionToSelect = announcementPublishOptions.immediateInfinite;
        }
      } else {
        publishOptionToSelect = announcementPublishOptions.draft;
      }

      form.setFieldsValue({
        title: announcement.title,
        message: announcement.message,
        scopes: announcement.scopes?.map(s => s.id),
        isPinned: announcement.isPinned,
        severity: announcement.severity.id,
        category: announcement.category.id,
        hasBanner: announcement.hasBanner,
        startDate,
        endDate,
        announcementPublishing: publishOptionToSelect,
      });
    }
  }, [announcement]);

  useDeepCompareEffect(() => {
    // If announcement open for edit, don't set wizardData defaults
    // -> wizard data could load later than announcement and override the announcement data
    if (!params.id) {
      const defaultCategory =
        wizardData?.categories?.find(c => c?.isDefault) ||
        wizardData?.categories?.[0];

      const defaultScope = wizardData?.serviceCenters?.find(s => s?.isDefault) || [];

      const defaultSeverity =
        wizardData?.severities?.find(s => s?.isDefault) ||
        wizardData?.severities?.[0];

      // 'none' option will always be there by MW
      const defaultTemplate =
        wizardData?.templates?.find(t => t?.isDefault) ||
        wizardData?.templates?.find(t => t?.uid === 'none');

      form.setFieldsValue({
        category: defaultCategory?.id,
        scopes: [defaultScope?.id],
        severity: defaultSeverity?.id,
      });
      handleTemplateChange(defaultTemplate?.id);
    }
  }, [wizardData, params.id]);

  const close = () => {
    dispatch(resetAnnouncementData());
    form.resetFields();
    navigate(-1);
  };

  useEffect(() => {
    if (updateAnnouncementSuccess) {
      dispatch(fetchBannerAnnouncement());
      close();
    }
  }, [updateAnnouncementSuccess]);

  const handleSave = () => {
    const formValues = form.getFieldsValue();

    const publishOption = formValues.announcementPublishing;
    let isPublished;
    let startDate;
    let endDate;

    if (publishOption === announcementPublishOptions.draft) {
      isPublished = false;
      startDate = null;
      endDate = null;
    } else if (publishOption === announcementPublishOptions.immediateInfinite) {
      isPublished = true;
      startDate = null;
      endDate = null;
    } else if (publishOption === announcementPublishOptions.specific) {
      isPublished = true;
      startDate = getFullDateFromFormValues(formValues.startDate).toISOString();
      endDate = getFullDateFromFormValues(formValues.endDate).toISOString();
    } else {
      // Failsafe
      isPublished = false;
      startDate = null;
      endDate = null;
    }

    const dataToSend = {
      title: formValues.title,
      message: formValues.message,
      scopes: formValues.scopes,
      isPublished,
      startDate,
      endDate,
      isPinned: formValues.isPinned,
      severity: formValues.severity,
      category: formValues.category,
      hasBanner: formValues.hasBanner,
    };

    if (params?.id) {
      dispatch(updateAnnouncement({ id: params?.id, ...dataToSend }));
    } else {
      dispatch(addAnnouncement(dataToSend));
    }
  };

  return (
    <SidePanel
      width={'40%'}
      title={intl.formatMessage({
        id: `announcements.${params.id ? 'EDIT_ANNOUNCEMENT' : 'ADD_ANNOUNCEMENT'}`,
      })}
      onPanelClose={close}
      contentOverflow={false}
    >
      {isSidePanelLoading ?
        <Spinner /> :
        <Form
          layout='horizontal'
          onFinish={handleSave}
          onFieldsChange={() => setIsFormTouched(true)}
          requiredMark={false}
          form={form}
          disabled={isUpdatingAnnouncement}
        >
          {/* Category */}
          <StyledRow>
            <Col span={24}>
              <FormField
                label={intl.formatMessage({
                  id: 'announcements.edit.TYPE',
                })}
                name='category'
                initialValue={null}
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'rules.ANNOUNCEMENT_TYPE' }),
                }]}
                wrapperCol={{ span: 7 }}
              >
                <Dropdown
                  values={wizardData?.categories || []}
                  valueKey='id'
                  valueLabel='description'
                  data-test-id='dropdown-announcement-type'
                />
              </FormField>
            </Col>
          </StyledRow>
          {/* Scope */}
          <StyledRow>
            <Col span={24}>
              <FormField
                label={intl.formatMessage({
                  id: 'announcements.edit.SCOPE',
                })}
                name='scopes'
                initialValue={[]}
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'rules.ANNOUNCEMENT_SCOPE' }),
                }]}
              >
                <Dropdown
                  multiSelect={true}
                  values={wizardData?.serviceCenters || []}
                  valueKey='id'
                  valueLabel='name'
                  data-test-id='dropdown-customer-center'
                />
              </FormField>
            </Col>
          </StyledRow>
          {/* Template */}
          <StyledRow>
            <Col span={24}>
              <FormField
                label={intl.formatMessage({
                  id: 'announcements.edit.TEMPLATE',
                })}
                name='template'
                initialValue={null}
                wrapperCol={{ span: 7 }}
              >
                <Dropdown
                  values={wizardData?.templates || []}
                  valueKey='id'
                  valueLabel='name'
                  onSelect={handleTemplateChange}
                />
              </FormField>
            </Col>
          </StyledRow>
          {/* Title */}
          <StyledRow>
            <Col span={24}>
              <FormField
                label={intl.formatMessage({
                  id: 'announcements.TITLE',
                })}
                name='title'
                initialValue=''
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'rules.ANNOUNCEMENT_TITLE' }),
                }]}
              >
                <Input data-test-id='input-announcement-title' />
              </FormField>
            </Col>
          </StyledRow>
          {/* HasBanner */}
          <StyledRow>
            <Col span={24}>
              <FormField
                name='hasBanner'
                valuePropName='checked'
                initialValue={false}
              >
                <Checkbox data-test-id='checkbox-activate-banner'>{intl.formatMessage({
                  id: 'announcements.edit.ACTIVATE_BANNER',
                })}</Checkbox>
              </FormField>
            </Col>
          </StyledRow>
          {/* Message */}
          <StyledRow>
            <Col span={24}>
              <FormField
                name='message'
                initialValue=''
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'rules.ANNOUNCEMENT_DESCRIPTION' }),
                }]}
                data-test-id='input-announcement-description'
              >
                <RichTextEditor width='100%' />
              </FormField>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={24}>
              <FormField
                label={intl.formatMessage({
                  id: 'announcements.SEVERITY',
                })}
                name='severity'
                initialValue={null}
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'rules.ANNOUNCEMENT_SEVERITY' }),
                }]}
                wrapperCol={{ span: 7 }}
              >
                <SeveritySelector
                  options={wizardData?.severities || []}
                />
              </FormField>
            </Col>
          </StyledRow>
          {/* Publishing option */}
          <StyledRow>
            <Col span={24}>
              <PublishOptionSelector
                selectedPublishOption={selectedPublishOption}
                fieldName='announcementPublishing'
                initialValue={announcementPublishOptions.draft}
              />
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={24}>
              <FormField
                name='isPinned'
                valuePropName='checked'
                initialValue={false}
              >
                <Checkbox data-test-id='checkbox-announcement-pin'>{intl.formatMessage({
                  id: 'announcements.edit.PIN',
                })}</Checkbox>
              </FormField>
            </Col>
          </StyledRow>
          <Buttons marginTop={20}>
            <StyledButton
              onClick={close}
              disabled={isUpdatingAnnouncement}
              data-test-id='btn-cancel-announcement'
            >
              {intl.formatMessage({ id: 'common.CANCEL' })}
            </StyledButton>
            <StyledButton
              htmlType='submit'
              type='secondary'
              disabled={isUpdatingAnnouncement || !isFormTouched}
              data-test-id='btn-save-announcement'
            >
              {intl.formatMessage({
                id: `announcements.${params.id ? 'SAVE_CHANGES' : 'ADD_ANNOUNCEMENT'}`,
              })}
            </StyledButton>
          </Buttons>
        </Form>
      }
    </SidePanel>
  );
};

EditAnnouncementSidepanel.propTypes = {
  intl: PropTypes.object,
};

export default injectIntl(EditAnnouncementSidepanel);
